import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Image, StyleSheet, Text, View } from 'react-native';
import Svg, { Defs, LinearGradient, Rect, Stop } from 'react-native-svg';

import { DuelGiftJpg } from '../../../assets/duels';
import {
	CategoryBackgroundActiveSvg,
	CategoryBackgroundSvg,
	CategoryBadgeActiveSvg,
	CategoryBadgeSvg,
	CategoryFeaturedActiveSvg,
	CategoryFeaturedSvg,
	CategoryFrameActiveSvg,
	CategoryFrameSvg,
} from '../../../assets/inventory';
import { useSvgId } from '../../../utils/hooks/useSvgId';
import { Pressable } from '../common/Touchable';
import {
	ACTIVE_FILL,
	CATEGORY_FILL,
	INVENTORY_FONT,
	inventoryColors,
	inventoryGlow,
	RADIUS_MD,
} from './inventoryTheme';

/**
 * Figma "Inventory categories" (4038:30482) and its "Tab / Category" master (4038:31008). The
 * file's note: '80x64 fixed; set instances to FILL inside the 358-wide categories row (12px side
 * padding, 4px gap) to get the 80.5 used in the screens.'
 */
export type InventoryCategoryKey = 'frame' | 'background' | 'badge' | 'featured' | 'rewards';

export const CATEGORIES_DESIGN_WIDTH = 358;
export const CATEGORIES_DESIGN_HEIGHT = 72;
const SIDE_PADDING = 12;
const GAP = 4;
const TAB_HEIGHT = 64;
const ICON_SIZE = 24;

function RewardsIcon({ width, height }: { width: number; height: number }) {
	return (
		<Image source={DuelGiftJpg} style={{ width, height, borderRadius: 4 }} resizeMode='contain' />
	);
}

const CATEGORIES: Array<{
	key: InventoryCategoryKey;
	label: string;
	labelKey: string;
	icon: React.ComponentType<{ width: number; height: number }>;
	activeIcon: React.ComponentType<{ width: number; height: number }>;
}> = [
	{
		key: 'frame',
		label: 'Profile Frame',
		labelKey: 'T_SITE_INVENTORY_CATEGORY_PROFILE_FRAME',
		icon: CategoryFrameSvg,
		activeIcon: CategoryFrameActiveSvg,
	},
	{
		key: 'background',
		label: 'Background',
		labelKey: 'T_SITE_INVENTORY_CATEGORY_BACKGROUND',
		icon: CategoryBackgroundSvg,
		activeIcon: CategoryBackgroundActiveSvg,
	},
	{
		key: 'badge',
		label: 'Badge',
		labelKey: 'T_SITE_INVENTORY_CATEGORY_BADGE',
		icon: CategoryBadgeSvg,
		activeIcon: CategoryBadgeActiveSvg,
	},
	{
		key: 'featured',
		label: 'Featured',
		labelKey: 'T_SITE_INVENTORY_CATEGORY_FEATURED',
		icon: CategoryFeaturedSvg,
		activeIcon: CategoryFeaturedActiveSvg,
	},
	{
		key: 'rewards',
		label: 'Rewards',
		labelKey: 'T_SITE_DUELS_REWARDS',
		icon: RewardsIcon,
		activeIcon: RewardsIcon,
	},
];

const TAB_WIDTH =
	(CATEGORIES_DESIGN_WIDTH - SIDE_PADDING * 2 - GAP * (CATEGORIES.length - 1)) / CATEGORIES.length;

type Props = {
	value: InventoryCategoryKey;
	onChange: (category: InventoryCategoryKey) => void;
	scale: number;
};

export const InventoryCategories = ({ value, onChange, scale }: Props) => {
	const styles = useMemo(() => createStyles(scale), [scale]);
	const { t } = useTranslation();
	const activeFillId = useSvgId('inventoryCategoryActive');
	const restFillId = useSvgId('inventoryCategoryRest');

	return (
		<View style={styles.row}>
			{CATEGORIES.map(category => {
				const isActive = category.key === value;
				const Icon = isActive ? category.activeIcon : category.icon;

				return (
					<Pressable
						key={category.key}
						accessibilityRole='tab'
						accessibilityState={{ selected: isActive }}
						onPress={() => onChange(category.key)}
						style={[styles.tab, isActive ? styles.tabActive : styles.tabRest]}
					>
						<View pointerEvents='none' style={StyleSheet.absoluteFill}>
							<Svg width='100%' height='100%'>
								<Defs>
									<LinearGradient id={activeFillId} x1='0' y1='0' x2='0' y2='1'>
										{ACTIVE_FILL.map(([offset, color]) => (
											<Stop key={offset} offset={offset} stopColor={color} />
										))}
									</LinearGradient>
									<LinearGradient id={restFillId} x1='0' y1='0' x2='0' y2='1'>
										{CATEGORY_FILL.map(([offset, color]) => (
											<Stop key={offset} offset={offset} stopColor={color} />
										))}
									</LinearGradient>
								</Defs>
								<Rect
									x='0'
									y='0'
									width='100%'
									height='100%'
									fill={`url(#${isActive ? activeFillId : restFillId})`}
								/>
							</Svg>
						</View>

						{/* Wrapped for the same painting-order reason as the modal tabs' icons. */}
						<View>
							<Icon width={ICON_SIZE * scale} height={ICON_SIZE * scale} />
						</View>
						<Text
							numberOfLines={1}
							adjustsFontSizeToFit
							style={[styles.label, isActive ? styles.labelActive : styles.labelRest]}
						>
							{t(category.labelKey, { defaultValue: category.label })}
						</Text>
					</Pressable>
				);
			})}
		</View>
	);
};

const createStyles = (scale: number) =>
	StyleSheet.create({
		row: {
			width: CATEGORIES_DESIGN_WIDTH * scale,
			height: CATEGORIES_DESIGN_HEIGHT * scale,
			flexDirection: 'row',
			paddingHorizontal: SIDE_PADDING * scale,
			gap: GAP * scale,
		},
		tab: {
			width: TAB_WIDTH * scale,
			height: TAB_HEIGHT * scale,
			alignItems: 'center',
			justifyContent: 'center',
			gap: 5 * scale,
			borderRadius: RADIUS_MD * scale,
			borderWidth: Math.max(1, 1 * scale),
			overflow: 'hidden',
		},
		tabRest: {
			borderColor: inventoryColors.border,
		},
		tabActive: {
			borderColor: inventoryColors.borderActive,
			...inventoryGlow.active,
		},
		// Sofia Sans SemiBold 11, line-height 1.4, centred.
		label: {
			maxWidth: (TAB_WIDTH - 6) * scale,
			fontFamily: INVENTORY_FONT,
			fontSize: 11 * scale,
			fontWeight: '600',
			lineHeight: 11 * 1.4 * scale,
			textAlign: 'center',
		},
		labelRest: {
			color: inventoryColors.secondary,
		},
		labelActive: {
			color: inventoryColors.primary,
		},
	});

export default InventoryCategories;
